import React, { Component, ErrorInfo, ReactNode } from 'react';

/**
 * Root ErrorBoundary: catches uncaught render/runtime errors in the tree below it
 * and shows a friendly recovery screen with a "Reload App" button.
 *
 *   <ErrorBoundary onError={(e, info) => Sentry.captureException(e, { extra: info })}>
 *     <App />
 *   </ErrorBoundary>
 *
 * Note: error boundaries catch errors during rendering, lifecycle methods and constructors.
 * They do NOT catch event-handler, async (promise) or SSR errors, so those still need try/catch.
 */

const COLORS = {
  ink: '#0c0c1a',
  green: '#16a34a',
  paper: '#fbfcfa',
  wash: 'rgba(22, 163, 74, 0.10)',
  muted: '#64617a',
};

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Hook for logging, e.g. Sentry. */
  onError?: (error: Error, info: ErrorInfo) => void;
  /** Where "Reload App" goes. Default: reload the current URL. */
  onReload?: () => void;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  private reload = () => {
    if (this.props.onReload) this.props.onReload();
    else window.location.reload();
  };

  private retry = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const c = COLORS;
    const isDev = typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production';
    const common = { stroke: c.ink, strokeWidth: 4, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const };

    return (
      <main
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          textAlign: 'center',
          background: c.paper,
          backgroundImage: 'radial-gradient(rgba(12,12,26,0.07) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          color: c.ink,
          fontFamily: 'var(--font-ui, Inter, system-ui, -apple-system, "Segoe UI", sans-serif)',
        }}
      >
        <style>{`
          @keyframes eb-hover{0%,100%{transform:translateY(0) rotate(-4deg)}50%{transform:translateY(-10px) rotate(3deg)}}
          @keyframes eb-shadow{0%,100%{transform:scaleX(1);opacity:.1}50%{transform:scaleX(.8);opacity:.06}}
          @keyframes eb-blink{0%,100%{opacity:.25;transform:scale(.7)}50%{opacity:1;transform:scale(1)}}
          @keyframes eb-rise{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
          .eb-plane{animation:eb-hover 3.2s ease-in-out infinite;transform-origin:100px 100px}
          .eb-shadow{animation:eb-shadow 3.2s ease-in-out infinite;transform-origin:100px 168px}
          .eb-spark{animation:eb-blink 2.2s ease-in-out infinite;transform-box:fill-box;transform-origin:center}
          .eb-in{animation:eb-rise .7s cubic-bezier(.2,.7,.2,1) both}
          .eb-btn{display:inline-flex;align-items:center;gap:8px;height:48px;padding:0 24px;border-radius:10px;
            font:500 15px/1 inherit;font-family:inherit;cursor:pointer;transition:transform .15s ease,box-shadow .15s ease,background .15s ease}
          .eb-btn:hover{transform:translateY(-1px)}
          .eb-btn:focus-visible{outline:2px solid ${c.green};outline-offset:3px}
          .eb-primary{background:${c.green};color:#fff;border:0;box-shadow:0 6px 16px -6px rgba(22,163,74,.55)}
          .eb-ghost{background:#fff;color:${c.ink};border:1px solid rgba(12,12,26,.12)}
          .eb-ghost:hover{background:#f6f8f5}
          @media (prefers-reduced-motion: reduce){
            .eb-plane,.eb-shadow,.eb-spark,.eb-in{animation:none}
            .eb-btn{transition:none}
          }
        `}</style>

        <svg width="180" height="180" viewBox="0 0 200 200" fill="none" aria-hidden="true" style={{ overflow: 'visible' }}>
          <circle cx="100" cy="100" r="74" fill={c.wash} />
          <ellipse className="eb-shadow" cx="100" cy="168" rx="34" ry="5" fill={c.ink} />
          <g className="eb-plane">
            {/* grounded, slightly bent paper plane */}
            <polygon points="40,62 164,98 92,104" fill={c.paper} {...common} />
            <polygon points="92,104 164,98 118,120 62,138" fill={c.ink} {...common} />
            <polygon points="92,104 126,110 76,132" fill={c.green} />
          </g>
          {/* little alert sparks */}
          <path className="eb-spark" d="M150 48 v14 M150 70 v2" stroke={c.green} strokeWidth={4} strokeLinecap="round" />
          <path className="eb-spark" style={{ animationDelay: '.7s' }} d="M32 76H44M38 70V82" stroke={c.ink} strokeWidth={3} strokeLinecap="round" />
        </svg>

        <p
          className="eb-in"
          style={{ margin: '20px 0 0', fontSize: 13, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: c.muted }}
        >
          Something went wrong
        </p>

        <h1
          className="eb-in"
          style={{
            animationDelay: '.08s',
            margin: '16px 0 0',
            fontFamily: 'var(--font-display, "Newsreader", Georgia, "Times New Roman", serif)',
            fontSize: 'clamp(34px, 6vw, 56px)',
            lineHeight: 1.08,
            fontWeight: 500,
            letterSpacing: '-0.02em',
          }}
        >
          We hit a bit of
          <br />
          <em style={{ color: c.green, fontStyle: 'italic' }}>turbulence.</em>
        </h1>

        <p
          className="eb-in"
          style={{ animationDelay: '.16s', margin: '20px auto 0', maxWidth: 440, fontSize: 17, lineHeight: 1.6, color: c.muted }}
        >
          An unexpected error interrupted the app. Your data is safe. Reload to get back on course.
        </p>

        <div className="eb-in" style={{ animationDelay: '.24s', marginTop: 28, display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button type="button" className="eb-btn eb-primary" onClick={this.reload}>
            Reload App
          </button>
          <button type="button" className="eb-btn eb-ghost" onClick={this.retry}>
            Try again
          </button>
        </div>

        {isDev && (
          <pre
            style={{
              marginTop: 32,
              maxWidth: 720,
              maxHeight: 180,
              overflow: 'auto',
              padding: 14,
              textAlign: 'left',
              fontSize: 12,
              lineHeight: 1.5,
              borderRadius: 10,
              background: '#fff',
              border: '1px solid rgba(12,12,26,.1)',
              color: c.muted,
            }}
          >
            {error.stack || error.message}
          </pre>
        )}
      </main>
    );
  }
}
